import { ChevronsUpDown, LogOut, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { CONFIG } from '@/config-globals';
import { usePathname } from '@/hooks/use-pathname';
import { useRouter } from '@/hooks/use-router';
import { ac } from '@/lib/permissions';
import { useIsAuthenticated, usePermissions, useSignOut } from '@/services/auth';
import { screenAllowsAction, useScreens } from '@/services/screens';
import { Icon } from './icon';
import { ContentLoader } from './loaders';

interface MenuItem {
  title: string;
  url: string;
  icon: React.ReactNode;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

export function AppSidebar() {
  const router = useRouter();
  const pathname = usePathname();
  const { mutate: signOut } = useSignOut();
  const { user } = useIsAuthenticated();
  const { hasPermission } = usePermissions();
  const { toggleSidebar, isMobile } = useSidebar();
  const { data: screens } = useScreens({ perPage: 100 });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(false);
  }, [pathname]);

  if (!user) return null;

  // Screens que são configuração do sistema (não cadastro de negócio) ficam em
  // "Configurações" em vez de "Cadastros".
  const CONFIG_SCREEN_RESOURCES = ['users', 'additional-options', 'companies'];

  const visibleScreens = (screens?.data ?? [])
    .filter((screen) => screen.showInMenu && screen.isActive)
    .filter((screen) => screenAllowsAction(screen, user.role, 'browse'))
    .sort((a, b) => a.menuOrder - b.menuOrder)
    .map((screen) => ({
      title: screen.pluralLabel,
      url: `/${screen.resource}`,
      icon: <Icon name={(screen.icon as any) || 'table'} className="size-4" />,
      resource: screen.resource,
    }));

  const screenItems: MenuItem[] = visibleScreens.filter(
    (screen) => !CONFIG_SCREEN_RESOURCES.includes(screen.resource),
  );
  const configScreenItems: MenuItem[] = visibleScreens.filter((screen) =>
    CONFIG_SCREEN_RESOURCES.includes(screen.resource),
  );

  const items: MenuSection[] = [
    {
      title: 'Geral',
      items: [
        {
          title: 'Dashboard',
          url: '/',
          icon: <Icon name="layout-dashboard" className="size-4" />,
        },
        ...(hasPermission('sales', 'browse')
          ? [
              {
                title: 'Pedidos',
                url: '/sales',
                icon: <Icon name="shopping-cart" className="size-4" />,
              },
            ]
          : []),
      ],
    },
    {
      title: 'Cadastros',
      items: screenItems,
    },
    {
      title: 'Configurações',
      items: [
        ...configScreenItems,
        // Link do editor de telas — usa 'update' (não 'browse') de propósito: o usuário
        // padrão também tem 'browse' em screens (precisa, pra montar o menu de Cadastros),
        // mas só quem pode editar a definição das telas (admin) deve ver esse atalho.
        ...(hasPermission('screens', 'update')
          ? [
              {
                title: 'Telas',
                url: '/screens',
                icon: <Icon name="layout-panel-top" className="size-4" />,
              },
            ]
          : []),
      ],
    },
  ];

  const handleLogout = () => {
    setIsLoading(true);
    setTimeout(() => {
      signOut();
    }, 500);
  };

  const handlePush = (url: string) => {
    if (pathname !== url) {
      setIsLoading(true);
      router.push(`/${url}`);
    }
    if (isMobile) toggleSidebar();
  };

  return (
    <Sidebar collapsible="icon">
      <ContentLoader show={isLoading} />
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => handlePush('/')}
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-white text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <img src="/logo.png" className="size-4" alt="Logo" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-extrabold">{CONFIG.APP_NAME}</span>
                <span className="truncate text-xs">by {CONFIG.APP_AUTHOR}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="overflow-x-auto bg-[linear-gradient(180deg,var(--primary-soft)_0%,transparent_320px)]">
        {items.map((section) => {
          const accessibleItems = section.items.filter((item) => {
            const resource = item.url === '/' ? 'dashboard' : item.url.split('/')[1]!;
            // Itens de "Cadastros" (screens dinâmicas) já vêm pré-filtrados por
            // screenAllowsAction — só recursos compilados passam pelo `ac` estático aqui.
            if (!(resource in ac.statements)) return true;
            return hasPermission(resource as keyof typeof ac.statements, 'browse');
          });

          if (accessibleItems.length === 0) return null;

          return (
            <SidebarGroup key={section.title}>
              <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
              <SidebarMenu>
                {accessibleItems.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      onClick={() => handlePush(item.url)}
                      tooltip={item.title}
                      isActive={
                        pathname === item.url || pathname.startsWith(`${item.url}/`)
                      }
                      className="text-sm font-semibold"
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user?.image ?? undefined} alt={user?.name} />
                    <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user?.name}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={user?.image ?? undefined} alt={user?.name} />
                      <AvatarFallback className="rounded-lg">
                        {user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{user?.name}</span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => router.push('/profile')}>
                    <User />
                    Meu Perfil
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
